import React, { useEffect, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { Search, UserPlus, Upload, Download, Users, Building2, Pencil, Trash2 } from "lucide-react";
import { get, post, put, del } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, Modal } from "../../../components/admin/EssUi";
import EmployeeAvatar from "../../../components/admin/EmployeeAvatar";

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";
const SOFT = "#eef1ee";

const INIT_FORM = { name: "", department_id: "", work_email: "", work_phone: "" };

const DetailRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${SOFT}` }}>
    <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{value || "—"}</span>
  </div>
);

const StatBadge = ({ label, value, color }) => (
  <div style={{ flex: 1, background: "#f8fafc", borderRadius: 8, padding: "10px 14px", textAlign: "center", border: `1px solid ${SOFT}` }}>
    <div style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 600, color: color || "#0f172a", marginTop: 2 }}>{value}</div>
  </div>
);

const ESSAdminEmployeesPage = () => {
  const intl = useIntl();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(INIT_FORM);
  const [detailEmp, setDetailEmp] = useState(null);
  const [leaves, setLeaves] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [empRes, deptRes, leaveRes] = await Promise.all([
        get("/api/employees?limit=500"),
        get("/api/departments"),
        get("/api/leave-requests"),
      ]);

      setEmployees(empRes?.data || []);
      setDepartments(deptRes?.data || []);
      setLeaves(leaveRes?.data || []);
    } catch {
      setEmployees([]);
      setDepartments([]);
      setLeaves([]);
    }
    setLoaded(true);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (loaded && location.state?.openAdd) {
      openAdd();
      window.history.replaceState({}, document.title);
    }
  }, [loaded, location.state?.openAdd]);

  const deptMap = {};
  departments.forEach((d) => { deptMap[d.id] = d.name || d.complete_name; });

  const activeEmps = employees.filter((e) => e.id != null);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const newHires = employees.filter((e) => e.created_at && new Date(e.created_at) >= monthStart);

  const onLeaveIds = new Set(leaves.map((l) => l.employee_id).filter(Boolean));

  const filtered = activeEmps.filter((e) => {
    if (deptFilter !== "all" && e.department_id !== Number(deptFilter)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(e.name || "").toLowerCase().includes(q) && !(e.work_email || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const groupByInitial = (emps) => {
    const groups = {};
    emps.forEach((e) => {
      const initial = (e.name || "?").charAt(0).toUpperCase();
      if (!groups[initial]) groups[initial] = [];
      groups[initial].push(e);
    });
    return Object.keys(groups).sort().reduce((acc, k) => { acc[k] = groups[k]; return acc; }, {});
  };

  const groupedEmployees = groupByInitial(filtered);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const vals = { name: form.name.trim() };
      if (form.department_id) vals.department_id = Number(form.department_id);
      if (form.work_email) vals.work_email = form.work_email;
      if (form.work_phone) vals.work_phone = form.work_phone;

      if (modal === "edit" && detailEmp) {
        await put(`/api/employees/${detailEmp.lewoo_uid}`, vals);
      } else {
        await post("/api/employees", vals);
      }
      setModal(null);
      setDetailEmp(null);
      setForm(INIT_FORM);
      setLoaded(false);
      await loadData();
    } catch (e) {
      alert("Error saving employee: " + (e.message || "Unknown"));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!detailEmp || !window.confirm(`Delete ${detailEmp.name}?`)) return;
    try {
      await del(`/api/employees/${detailEmp.lewoo_uid}`);
      setDetailEmp(null);
      setModal(null);
      setLoaded(false);
      await loadData();
    } catch (e) {
      alert("Error deleting employee: " + (e.message || "Unknown"));
    }
  };

  const openDetail = (emp) => {
    setDetailEmp(emp);
    setModal("detail");
  };

  const openEditForm = () => {
    setForm({
      name: detailEmp.name || "",
      department_id: detailEmp.department_id ? String(detailEmp.department_id) : "",
      work_email: detailEmp.work_email || "",
      work_phone: detailEmp.work_phone || "",
    });
    setModal("edit");
  };

  const openAdd = () => {
    setForm(INIT_FORM);
    setDetailEmp(null);
    setModal("add");
  };

  const renderForm = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Name *</label>
        <input className="ess-inp" style={{ width: "100%" }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
      </div>
       <div>
         <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Department</label>
         <select className="ess-inp" style={{ width: "100%" }} value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
           <option value="">— Select —</option>
           {departments.map((d) => <option key={d.id} value={d.id}>{deptMap[d.id]}</option>)}
         </select>
       </div>
       <div style={{ display: "grid", gap: 12 }} className="ess-rg-2">
         <div>
           <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Email</label>
           <input className="ess-inp" style={{ width: "100%" }} value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} placeholder="email@example.com" />
         </div>
         <div>
           <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Phone</label>
           <input className="ess-inp" style={{ width: "100%" }} value={form.work_phone} onChange={(e) => setForm({ ...form, work_phone: e.target.value })} placeholder="+237 XXX XXX XXX" />
         </div>
       </div>
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  const onLeaveEmps = activeEmps.filter((e) => onLeaveIds.has(e.id));

  const filterButtons = [
    { key: "all", label: "Directory", count: activeEmps.length },
    { key: "onleave", label: "On Leave", count: onLeaveEmps.length },
    { key: "newhires", label: "New Hires", count: newHires.length },
    { key: "orgchart", label: "Org Chart" },
  ];

  const renderOrgTree = () => {
    if (activeEmps.length === 0) return (
      <div className="ess-empty">No employees to display.</div>
    );
    return activeEmps.map((emp) => {
      const deptName = deptMap[emp.department_id] || (departments.find((d) => d.id === emp.department_id)?.name) || "—";
      return (
        <div key={emp.id} className="flex items-center gap-3 p-3 my-1 rounded-md border bg-white hover:bg-slate-50 transition-colors" style={{ borderColor: SOFT, maxWidth: 360 }} onClick={() => openDetail(emp)}>
          <EmployeeAvatar employee={emp} size={36} fontSize={11} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{emp.name}</p>
            <p className="text-xs text-slate-500 truncate">{deptName}</p>
          </div>
          <span className="text-xs font-medium text-slate-400 flex-shrink-0">#{String(emp.id).padStart(4, "0")}</span>
        </div>
      );
    });
  };

const EMP_CARD = (emp, i) => {
    const deptName = deptMap[emp.department_id] || (departments.find((d) => d.id === emp.department_id)?.name) || "—";
    return (
      <div key={emp.id} className="ess-emp-card" onClick={() => openDetail(emp)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14 }}>
        <EmployeeAvatar employee={emp} size={40} fontSize={14} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{deptName}</div>
        </div>
        <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>#{String(emp.id).padStart(4, "0")}</span>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Employee Directory"
        subtitle="Manage all employees and their profiles"
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={openAdd} className="ess-btn ess-btn-primary">
              <UserPlus size={15} />
              Add Employee
            </button>
            <button className="ess-btn ess-btn-secondary">
              <Upload size={15} />
              Import
            </button>
            <button className="ess-btn ess-btn-secondary">
              <Download size={15} />
              Export
            </button>
          </div>
        }
      />

<div className="ess-sg ess-sg4" style={{ marginBottom: 20 }}>
         <StatCard label="Total Employees" value={String(activeEmps.length)} icon={Users} />
         <StatCard label="Departments" value={String(departments.length)} icon={Building2} />
         <StatCard label="New This Month" value={String(newHires.length)} icon={UserPlus} trend={`↑ ${newHires.length} joined`} />
       </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px 24px", marginBottom: 20, borderBottom: `1px solid ${BORDER}` }}>
        {filterButtons.map((btn) => (
          <button key={btn.key} onClick={() => setActiveFilter(btn.key)} style={{
            padding: "9px 2px 9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${activeFilter === btn.key ? GREEN : "transparent"}`,
            marginBottom: -1,
            color: activeFilter === btn.key ? GREEN : "#64748b", fontSize: 13,
            fontWeight: activeFilter === btn.key ? "600" : "500", cursor: "pointer", transition: "color 0.15s",
            display: "flex", alignItems: "center", gap: 6
          }}>
            {btn.label}
            {btn.count !== undefined && (
              <span style={{ background: activeFilter === btn.key ? "#f0f7f0" : "#f1f5f9", color: activeFilter === btn.key ? GREEN : "#64748b", padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: "600" }}>{btn.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="ess-fbar" style={{ padding: 16, marginBottom: 24, background: "white", borderRadius: 8, border: `1px solid ${BORDER}` }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name..." style={{ width: "100%", padding: "9px 12px 9px 36px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13.5, outline: "none", background: "#fff" }} />
        </div>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13.5, background: "#fff", outline: "none", cursor: "pointer", minWidth: 160 }}>
          <option value="all">All Departments</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{deptMap[d.id]}</option>)}
        </select>
      </div>

      {activeFilter === "orgchart" ? (
        <div style={{ background: "white", borderRadius: 8, border: `1px solid ${BORDER}`, padding: 16 }}>{renderOrgTree()}</div>
      ) : activeFilter === "onleave" ? (
        onLeaveEmps.length === 0 ? (
          <div className="ess-empty">No employees on leave currently.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>{onLeaveEmps.map((emp, i) => EMP_CARD(emp, i))}</div>
        )
      ) : activeFilter === "newhires" ? (
        newHires.length === 0 ? (
          <div className="ess-empty">No new hires this month.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>{newHires.map((emp, i) => EMP_CARD(emp, i))}</div>
        )
      ) : (
        <div>
          {Object.keys(groupedEmployees).length === 0 ? (
            <div className="ess-empty">No employees found.</div>
          ) : (
            Object.keys(groupedEmployees).sort().map((initial) => (
              <div key={initial} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", paddingBottom: 10, borderBottom: `1px solid ${BORDER}`, marginBottom: 14 }}>{initial}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {groupedEmployees[initial].map((emp, i) => EMP_CARD(emp, i))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <Modal open={modal === "detail"} onClose={() => { setModal(null); setDetailEmp(null); }} title="" size="lg">
        {detailEmp && (
          <div>
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              <div style={{ flexShrink: 0 }}>
                <EmployeeAvatar employee={detailEmp} size={80} fontSize={28} />
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", background: "#f1f5f9", padding: "2px 10px", borderRadius: 10 }}>#{String(detailEmp.id).padStart(4, "0")}</span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 20, fontWeight: 600, color: "#0f172a", margin: 0 }}>{detailEmp.name}</h3>
                <p style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>{(deptMap[detailEmp.department_id] || departments.find((d) => d.id === detailEmp.department_id)?.name) || "—"}</p>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12, marginBottom: 20 }} className="ess-rg-2">
              <StatBadge label="Department" value={(deptMap[detailEmp.department_id] || departments.find((d) => d.id === detailEmp.department_id)?.name) || "—"} color={GREEN} />
            </div>

            <div style={{ background: "#f8fafc", borderRadius: 8, padding: 16, border: `1px solid ${SOFT}`, marginBottom: 20 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Contact</p>
              <DetailRow label="Email" value={detailEmp.work_email || "—"} />
              <DetailRow label="Phone" value={detailEmp.work_phone || "—"} />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20, borderTop: `1px solid ${SOFT}`, paddingTop: 16 }}>
              <button onClick={() => { setModal(null); setDetailEmp(null); }} className="ess-btn ess-btn-secondary">Close</button>
              <button onClick={openEditForm} className="ess-btn ess-btn-primary"><Pencil size={14} /> Edit</button>
              <button onClick={handleDelete} className="ess-btn ess-btn-danger"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={modal === "edit"} onClose={() => { setModal("detail"); }} title="Edit Employee" size="md">
        {renderForm()}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20, borderTop: `1px solid ${SOFT}`, paddingTop: 16 }}>
          <button onClick={() => setModal("detail")} className="ess-btn ess-btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()} className="ess-btn ess-btn-primary">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal>

      <Modal open={modal === "add"} onClose={() => { setModal(null); setDetailEmp(null); }} title="New Employee" size="md">
        {renderForm()}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20, borderTop: `1px solid ${SOFT}`, paddingTop: 16 }}>
          <button onClick={() => { setModal(null); setDetailEmp(null); }} className="ess-btn ess-btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()} className="ess-btn ess-btn-primary">
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ESSAdminEmployeesPage;